'use client';
import { useEffect, useState } from 'react';

import { Village, VillageDeal, VillagePriceVariants } from '@/services/types';
import Button from '../Button';
import VillageSideColumn from '../VillageSideColumn';
import VillageSideColumnContent from '../VillageSideColumn/VillageSideColumnContent';

import IVillageSideColumnDictionary from '@/dictionaries/components/villageSideColumn/types';
import { IEnquiryFormDictionary } from '@/dictionaries/components/enquiryForm/types';
import { ICountriesDictionary } from '@/dictionaries/utils/countries/types';
import { IModalsDictionary } from '@/dictionaries/components/modals/types';
import { IQuantitiesInputDictonary } from '@/dictionaries/components/quantitiesInput/types';
import { IDatePickerDictonary } from '@/dictionaries/components/datePicker/types';
import { IContactPageFormDictionary } from '@/dictionaries/pages/contacts/types';
import { IRegionZoneDictonary } from '@/dictionaries/utils/region-zone/types';
import IBookVillageDictionary from '@/dictionaries/pages/bookVillage/types';
import useVillageContext from '@/hooks/useVillageContext';
import { useChat } from '@/hooks/useChatContext';
import useVillage from '@/hooks/useVillage';
import { formatLocalizedNumber } from '@/utils/village';

import './styles.css';
import { useCurrency } from '@/contexts/currencyContext';
import { RiLoader2Fill } from 'react-icons/ri';

type Props = {
  priceVariants: VillagePriceVariants[];
  lang: string;
  village: Village;
  deals: VillageDeal[];
  dicts: {
    datePickerDict: IDatePickerDictonary;
    quantitiesInputDict: IQuantitiesInputDictonary;
    formDict: IContactPageFormDictionary;
    regionZoneDict: IRegionZoneDictonary;
    villageSideColumnDict: IVillageSideColumnDictionary;
    bookVillageDict: IBookVillageDictionary;
    enquiryDict: IEnquiryFormDictionary;
    countriesDict: ICountriesDictionary;
    modalsDict: IModalsDictionary;
  };
};

export default function VillageMobileFooter({
  village,
  lang,
  dicts,
  priceVariants,
  deals,
}: Props) {
  const { book, from, now, request } = dicts.villageSideColumnDict;
  const [openBooking, setOpenBooking] = useState(false);
  const [bookingPrice, setBookingPrice] = useState<string>();
  const [discountInfos, setDiscountInfos] = useState<{
    value: number;
    message?: string;
  }>();
  const [isLoading, setIsLoading] = useState(false);

  const { currency, applyCurrency } = useCurrency();
  const { setHideChat } = useChat();

  const villaInPuglia = village.zone.region.code === 'puglia';

  const defaultPrice =
    village.calculatedPrice || parseInt(village?.prices[0]?.originalPrice);

  const {
    selectedDates,
    selectedPeriod,
    priceVariant,
    extras,
    handleExtras,
    totalGuests,
    totalBedrooms,
  } = useVillageContext();

  const { getOriginalPrice, countNights, getVillagePrice } = useVillage({
    selectedDates,
    selectedPeriod,
    priceVariant,
    village,
  });

  const datesAreSelected = selectedDates.start && selectedDates.end;
  const canShowPrice = selectedPeriod && datesAreSelected && priceVariant;

  function getPrice() {
    if (canShowPrice) {
      setIsLoading(false);
      const { originalPrice, rawPrice, dailyPrices } = getOriginalPrice(
        village,
        selectedDates,
        totalBedrooms,
        totalGuests,
      );

      const { price, deal } = getVillagePrice(
        rawPrice,
        deals,
        dailyPrices,
        selectedDates.end,
      );

      // getVillagePrice only returns a deal that applies to this stay
      const dealCanBeApplied = deal !== null;

      if (dealCanBeApplied) {
        const dealTranslation = deal.deal.translation.find(
          (translation) => translation.locale === lang,
        );
        setDiscountInfos({
          value: originalPrice,
          message: dealTranslation?.title,
        });
      } else {
        setDiscountInfos(undefined);
      }

      let extrasTotal = extras
        .filter((extra) => !extra.payInVilla)
        .reduce((acc, extra) => {
          return acc + extra.computedPrice;
        }, 0);

      const totalPrice = dealCanBeApplied ? price : originalPrice;

      const formatedPrice = formatLocalizedNumber(
        applyCurrency(totalPrice + extrasTotal),
        lang,
      );

      setBookingPrice(formatedPrice);
    } else {
      setIsLoading(true);
    }
    return 0;
  }

  function formatVillagePrice(price: number) {
    return formatLocalizedNumber(applyCurrency(price), lang);
  }

  useEffect(() => {
    if (openBooking) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.add('vmf-hide-scroll');
      root.classList.add('vmf-hide-scroll');
      setHideChat(true);
    }

    if (!openBooking) {
      var root = document.getElementsByTagName('html')[0];
      document.body.classList.remove('vmf-hide-scroll');
      root.classList.remove('vmf-hide-scroll');
      setHideChat(false);
    }
    // eslint-disable-next-line
  }, [openBooking]);

  useEffect(() => {
    if (extras.length > 0) {
      handleExtras(
        extras.map((extra) => {
          if (!extra.isFlatPrice) {
            const days = Math.max(countNights(selectedDates), extra.minNights);
            const computedPrice =
              Math.round(Number(extra.price) * days) * extra.selectedQuantity;
            return { ...extra, computedPrice };
          }

          return extra;
        }),
      );
    }
    // eslint-disable-next-line
  }, [selectedPeriod, selectedDates, priceVariant]);

  useEffect(() => {
    getPrice();
    // eslint-disable-next-line
  }, [selectedDates, extras, priceVariant]);

  return (
    <>
      {openBooking && (
        <div className="fixed top-0 left-0 z-[999] w-full max-w-full h-[100vh] overflow-y-auto bg-[rgba(0,0,0,.8)] desktop:hidden">
          <VillageSideColumn
            dicts={{ regionZoneDict: dicts.regionZoneDict }}
            village={village}
            isMobile
            handleCloseModal={() => setOpenBooking(false)}>
            <VillageSideColumnContent
              villaInPuglia={villaInPuglia}
              village={village}
              handleCloseModal={() => setOpenBooking(false)}
              dicts={dicts}
            />
          </VillageSideColumn>
        </div>
      )}
      <div className="fixed bottom-0 right-0 z-10 w-full h-[65px] flex items-start bg-white text-graphite shadow-[0_-10px_20px_0_rgba(0,0,0,.1)] desktop:hidden">
        <div className="w-full px-[0.9375rem] h-full max-w-default">
          <div className="w-full h-full flex flex-wrap justify-between items-center">
            <div className="flex flex-col gap-1 flex-wrap items-start justify-between">
              {!canShowPrice ? (
                <>
                  <span>{from}</span>
                  <h3 className="text-medium leading-none font-title">
                    {`${currency.symbol} ${
                      canShowPrice
                        ? bookingPrice
                        : formatVillagePrice(defaultPrice)
                    }`}
                  </h3>
                </>
              ) : isLoading ? (
                <>
                  <RiLoader2Fill className="animate-spin size-6 fill-primary" />
                </>
              ) : (
                <>
                  {discountInfos && (
                    <h4 className="text-gray-500 text-xs">
                      <span className="line-through font-title">{`${
                        currency.symbol
                      } ${formatLocalizedNumber(
                        applyCurrency(discountInfos.value),
                        lang,
                      )}`}</span>
                    </h4>
                  )}

                  <h3 className="text-medium leading-none font-title">
                    {`${currency.symbol} ${
                      canShowPrice
                        ? bookingPrice
                        : formatVillagePrice(defaultPrice)
                    }`}
                  </h3>
                </>
              )}
            </div>
            <Button
              onClick={() => setOpenBooking(true)}
              className="ml-auto w-fit !max-h-[calc(100%-1rem)] py-[16px] px-[20px] leading-[1]">
              {villaInPuglia ? request : lang == 'de' ? now : book}
            </Button>
          </div>
        </div>
      </div>
    </>
  );
}
